import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { X, Github, CheckCircle2, BookOpen, Loader2, Globe, Shield, ExternalLink, Clock, AlertTriangle, Sparkles, Square, CheckSquare, Settings, Trash2, BookMarked, Eye, Edit3, Wand2, Send, MessageSquare, ArrowDownToLine, RefreshCw, ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';
import { GitHubStatus, getGitHubInstallUrl, linkGitHubInstallation, saveGitHubRepoDescription, saveGroupOrder, generateRepoDescription, refineRepoDescription, generateNotebookDescription, getPublishedNotebookFolders, unpublishNotebookFromGitHub, chatWithReadmeAI, regenerateReadme } from '../services/githubService';
import { Language } from '../types';
import { getFirestore, collection, getDocs, orderBy, query, limit } from 'firebase/firestore';

// ── Lightweight GitHub-flavored-Markdown preview (no deps) ─────────────────────
// Not a full CommonMark implementation — just enough of what people actually put in
// a README (headers, bold/italic/strikethrough, inline code, links, lists, quotes,
// code fences, hr) so users who don't know the "**bold**" syntax can see the result
// before pushing to GitHub, rather than reading raw asterisks in the modal.
function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function renderInlineMarkdown(text: string): string {
    let s = escapeHtml(text);
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    s = s.replace(/\*([^*\s][^*]*?)\*/g, '<em>$1</em>');
    s = s.replace(/~~([^~]+)~~/g, '<del>$1</del>');
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    return s;
}
function renderMarkdownPreview(md: string): string {
    const lines = (md || '').split('\n');
    const out: string[] = [];
    let inCode = false;
    let codeBuf: string[] = [];
    let list: { type: 'ul' | 'ol'; items: string[] } | null = null;
    let para: string[] = [];

    const flushPara = () => { if (para.length) { out.push(`<p>${renderInlineMarkdown(para.join(' '))}</p>`); para = []; } };
    const flushList = () => {
        if (list) {
            const tag = list.type;
            out.push(`<${tag}>${list.items.map(i => `<li>${renderInlineMarkdown(i)}</li>`).join('')}</${tag}>`);
            list = null;
        }
    };

    for (const line of lines) {
        if (line.trim().startsWith('```')) {
            if (inCode) { out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`); codeBuf = []; inCode = false; }
            else { flushPara(); flushList(); inCode = true; }
            continue;
        }
        if (inCode) { codeBuf.push(line); continue; }
        if (!line.trim()) { flushPara(); flushList(); continue; }

        const h = line.match(/^(#{1,6})\s+(.*)$/);
        if (h) { flushPara(); flushList(); const lvl = h[1].length; out.push(`<h${lvl}>${renderInlineMarkdown(h[2])}</h${lvl}>`); continue; }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { flushPara(); flushList(); out.push('<hr/>'); continue; }

        const bq = line.match(/^>\s?(.*)$/);
        if (bq) { flushPara(); flushList(); out.push(`<blockquote>${renderInlineMarkdown(bq[1])}</blockquote>`); continue; }

        const ul = line.match(/^\s*[-*+]\s+(.*)$/);
        if (ul) { flushPara(); if (!list || list.type !== 'ul') { flushList(); list = { type: 'ul', items: [] }; } list.items.push(ul[1]); continue; }

        const ol = line.match(/^\s*\d+\.\s+(.*)$/);
        if (ol) { flushPara(); if (!list || list.type !== 'ol') { flushList(); list = { type: 'ol', items: [] }; } list.items.push(ol[1]); continue; }

        flushList();
        para.push(line.trim());
    }
    flushPara(); flushList();
    if (inCode && codeBuf.length) out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`);
    return out.join('\n');
}

const MARKDOWN_CHEATSHEET: { syntax: string; result: string }[] = [
    { syntax: '# Tiêu đề', result: 'Tiêu đề lớn (H1)' },
    { syntax: '## Tiêu đề phụ', result: 'Tiêu đề vừa (H2)' },
    { syntax: '**đậm**', result: 'chữ đậm' },
    { syntax: '*nghiêng*', result: 'chữ nghiêng' },
    { syntax: '[chữ](https://url)', result: 'link' },
    { syntax: '- mục 1\\n- mục 2', result: 'danh sách' },
    { syntax: '`code`', result: 'code' },
    { syntax: '> trích dẫn', result: 'blockquote' },
];

interface NotebookInfo {
    id: string;
    title: string;
    nodeCount?: number;
    description?: string;
    githubGroup?: string;
    lastPublishUrl?: string | null;
    lastPublishedAt?: number | null;
    lastPublishedNodeCount?: number | null;
    latestNodeUpdatedAt?: number | null;
    notebookUpdatedAt?: number | null;
    changedNodeCount?: number | null;
}

interface Props {
    githubStatus: GitHubStatus | null;
    notebooks: NotebookInfo[];
    language?: Language;
    onClose: () => void;
    onUpdateGitHubRepo: (repo: string, branch: string) => Promise<void>;
    onClearGitHubConfig: () => Promise<void>;
    onPublishNotebook: (notebookId: string) => void;
    onBulkPublish: (ids: string[], mode: 'full' | 'safe', commitMessage?: string) => void;
    onUpdateNotebookDescription: (notebookId: string, description: string) => void;
    onUpdateNotebookGroup: (notebookId: string, group: string) => void;
    onUnpublishNotebook?: (notebookId: string) => void;
    userId?: string;
}

interface DiaryNotebook {
    title: string;
    changedCount: number;
    totalNodeCount: number;
    isFirstPublish: boolean;
    repoUrl?: string;
}

interface DiaryEntry {
    publishedAt: number;
    dayKey?: string;
    // New consolidated daily format
    notebooks?: DiaryNotebook[];
    // Legacy per-notebook format
    notebookTitle?: string;
    changedCount?: number;
    totalNodeCount?: number;
    isFirstPublish?: boolean;
    aiSummary: string | null;
}

export const GitHubHubModal: React.FC<Props> = ({
    githubStatus, notebooks, language, onClose,
    onUpdateGitHubRepo, onClearGitHubConfig, onPublishNotebook, onBulkPublish, onUpdateNotebookDescription, onUpdateNotebookGroup, onUnpublishNotebook,
    userId,
}) => {
    const vn = language === 'vietnamese';
    const [ghRepo, setGhRepo] = useState(githubStatus?.repo || '');
    const [ghBranch, setGhBranch] = useState(githubStatus?.branch || 'main');
    const [savingRepo, setSavingRepo] = useState(false);
    const [savedRepo, setSavedRepo] = useState(false);
    const [editingDesc, setEditingDesc] = useState<Record<string, string>>({});
    const [repoDesc, setRepoDesc] = useState(githubStatus?.repoDescription || '');
    const [generatingDesc, setGeneratingDesc] = useState(false);
    const [aiLang, setAiLang] = useState<'en' | 'vi' | 'both'>('en');
    const [generatingNbDesc, setGeneratingNbDesc] = useState<string | null>(null);
    const [checked, setChecked] = useState<Set<string>>(
        () => new Set(notebooks.filter(nb => nb.lastPublishedAt).map(nb => nb.id))
    );
    const [publishedFolders, setPublishedFolders] = useState<Set<string>>(new Set());
    const [bulkMode, setBulkMode] = useState<'full' | 'safe'>('full');
    const [manualId, setManualId] = useState('');
    const [manualLinking, setManualLinking] = useState(false);
    const [manualError, setManualError] = useState('');
    const [confirmScreen, setConfirmScreen] = useState(false);
    const [commitMessage, setCommitMessage] = useState('');
    const [repoSaveConfirm, setRepoSaveConfirm] = useState(false);
    const [repoDescExpanded, setRepoDescExpanded] = useState(false);
    const [readmeEditorOpen, setReadmeEditorOpen] = useState(false);
    const [readmePreviewMode, setReadmePreviewMode] = useState(false);
    const [refineHint, setRefineHint] = useState('');
    const [refining, setRefining] = useState(false);
    const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const [readmeViewMode, setReadmeViewMode] = useState<'edit' | 'preview'>('edit');
    const [showMdHelp, setShowMdHelp] = useState(false);
    const [expandedNbDesc, setExpandedNbDesc] = useState<string | null>(null);
    const [editingGroup, setEditingGroup] = useState<Record<string, string>>({});
    const [expandedNbGroup, setExpandedNbGroup] = useState<string | null>(null);
    const [localGroupOrder, setLocalGroupOrder] = useState<string[]>(githubStatus?.groupOrder || []);

    const allGroups = useMemo(() => {
        const seen = new Set<string>();
        const groups: string[] = [];
        for (const g of localGroupOrder) {
            if (g && !seen.has(g)) { seen.add(g); groups.push(g); }
        }
        for (const nb of notebooks) {
            const g = nb.githubGroup?.trim();
            if (g && !seen.has(g)) { seen.add(g); groups.push(g); }
        }
        return groups;
    }, [notebooks, localGroupOrder]);
    const [settingsOpen, setSettingsOpen] = useState(!(!!githubStatus?.configured && !!githubStatus?.repo));
    const [localPublishedCounts, setLocalPublishedCounts] = useState<Record<string, number>>({});
    const [unpublishing, setUnpublishing] = useState<string | null>(null);
    const [unpublishConfirm, setUnpublishConfirm] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'notebooks' | 'diary'>('notebooks');
    const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[]>([]);
    const [diaryLoading, setDiaryLoading] = useState(false);

    useEffect(() => {
        if (activeTab !== 'diary' || !userId) return;
        setDiaryLoading(true);
        const db = getFirestore();
        getDocs(query(collection(db, 'users', userId, 'publishHistory'), orderBy('publishedAt', 'desc'), limit(500)))
            .then(snap => setDiaryEntries(snap.docs.map(d => d.data() as DiaryEntry)))
            .catch(e => Sentry.captureException(e, { tags: { feature: 'diary' } }))
            .finally(() => setDiaryLoading(false));
    }, [activeTab, userId]);

    const connected = !!githubStatus?.installationId;
    const configured = !!githubStatus?.configured && !!githubStatus?.repo;

    const toSnakeCase = (s: string) => (s.replace(/[^\w\s-]/g, '').trim().replace(/[-\s]+/g, '_').toLowerCase()) || 'untitled';

    const isNewNb = (nb: NotebookInfo) => {
        // Firestore lastPublishedAt is the source of truth — if it's set, notebook was published
        if (nb.lastPublishedAt) return false;
        // Session-local: just published in this session
        if (localPublishedCounts[nb.id] != null) return false;
        // Fallback: check GitHub folder list (handles edge case where Firestore lags)
        return !publishedFolders.has(toSnakeCase(nb.title));
    };

    const getLastCount = (nb: NotebookInfo) =>
        localPublishedCounts[nb.id] ?? nb.lastPublishedNodeCount;

    const isUnchanged = (nb: NotebookInfo) => {
        if (isNewNb(nb)) return false;
        // If notebook-level data (cross-links, title, description) changed after last publish
        if (nb.lastPublishedAt && nb.notebookUpdatedAt && nb.notebookUpdatedAt > nb.lastPublishedAt) return false;
        const lastCount = getLastCount(nb);
        if (lastCount != null && nb.nodeCount != null) {
            // We have both counts — compare them
            if (nb.nodeCount !== lastCount) return false;
            // Count same — check if any note was edited after last publish
            if (nb.lastPublishedAt && nb.latestNodeUpdatedAt && nb.latestNodeUpdatedAt > nb.lastPublishedAt) return false;
            return true;
        }
        // lastCount unknown — fall back to timestamp: no edits since last publish = unchanged
        if (nb.lastPublishedAt && nb.latestNodeUpdatedAt) {
            return nb.latestNodeUpdatedAt <= nb.lastPublishedAt;
        }
        // Can't tell — if it's been published before, assume unchanged (avoid false "+N notes")
        return !!nb.lastPublishedAt;
    };

    const nbStatus = (nb: NotebookInfo): 'new' | 'changed' | 'unchanged' => {
        if (isNewNb(nb)) return 'new';
        if (isUnchanged(nb)) return 'unchanged';
        return 'changed';
    };

    const buildCommitMessage = (selected: NotebookInfo[]) => {
        const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const newNbs = selected.filter(nb => nbStatus(nb) === 'new');
        const changedNbs = selected.filter(nb => nbStatus(nb) === 'changed');
        const parts: string[] = [];
        if (newNbs.length) parts.push(`publish ${newNbs.map(nb => nb.title).join(', ')}`);
        if (changedNbs.length) parts.push(`update ${changedNbs.map(nb => nb.title).join(', ')}`);
        if (!parts.length) parts.push(`re-publish ${selected.map(nb => nb.title).join(', ')}`);
        return `[StudyBoard] ${date}: ${parts.join('; ')}`;
    };

    useEffect(() => {
        if (!configured) return;
        getPublishedNotebookFolders()
            .then(folders => {
                const folderSet = new Set(folders);
                setPublishedFolders(folderSet);
                // Check NEW + CHANGED, skip UNCHANGED (no new notes since last publish)
                const toCheck = notebooks.filter(nb => {
                    const published = !!nb.lastPublishedAt || folderSet.has(toSnakeCase(nb.title));
                    if (!published) return true; // NEW → check
                    const unchanged = nb.lastPublishedNodeCount != null && nb.nodeCount != null && nb.nodeCount === nb.lastPublishedNodeCount;
                    return !unchanged; // CHANGED → check, UNCHANGED → skip
                });
                setChecked(new Set(toCheck.map(nb => nb.id)));
            })
            .catch(() => {
                setChecked(new Set(notebooks.filter(nb => nb.lastPublishedAt).map(nb => nb.id)));
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDate = (ts: number) => {
        const d = new Date(ts);
        return d.toLocaleDateString(vn ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const insertAtCursor = (text: string) => {
        const ta = textareaRef.current;
        if (!ta) { setRepoDesc(prev => prev ? prev + '\n\n' + text : text); return; }
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        setRepoDesc(repoDesc.slice(0, start) + text + repoDesc.slice(end));
        setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + text.length; ta.focus(); }, 0);
    };

    const handleChatSend = async () => {
        if (!chatInput.trim() || chatLoading) return;
        const userMsg = { role: 'user' as const, content: chatInput.trim() };
        const updated = [...chatMessages, userMsg];
        setChatMessages(updated);
        setChatInput('');
        setChatLoading(true);
        try {
            const reply = await chatWithReadmeAI(updated, repoDesc);
            setChatMessages(prev => [...prev, { role: 'assistant', content: reply }]);
        } catch (e: any) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${e.message || 'AI error'}` }]);
        } finally {
            setChatLoading(false);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    };

    const quickChips = vn
        ? ['Viết giới thiệu repo', 'Thêm phần song ngữ EN+VI', 'Liệt kê các topic tôi học', 'Tạo opening truyền cảm hứng', 'Thêm mục Quick start']
        : ['Write a repo intro', 'Add bilingual EN+VI section', 'List my learning topics', 'Make it motivational', 'Add quick start guide'];

    return (
        <>
        <div className="fixed inset-0 z-[10200] flex items-center justify-center bg-black/50 backdrop-blur-sm md:p-4">
            <div className="bg-white dark:bg-slate-800 md:rounded-2xl shadow-2xl md:border dark:border-slate-700 w-full md:max-w-xl h-full md:h-auto md:max-h-[88vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900 shrink-0">
                    <Github size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 dark:text-white leading-tight">GitHub Publishing Hub</p>
                        {configured && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate leading-tight mt-0.5">
                                {githubStatus!.connectedAs ? `@${githubStatus!.connectedAs} · ` : ''}
                                <a href={`https://github.com/${githubStatus!.repo}`} target="_blank" rel="noreferrer"
                                    className="text-blue-400 hover:underline">
                                    {githubStatus!.repo}
                                </a>
                                {' / '}{githubStatus!.branch}
                            </p>
                        )}
                    </div>
                    {connected && (
                        <button
                            onClick={() => setSettingsOpen(v => !v)}
                            className={`p-1.5 rounded-lg transition-colors ${settingsOpen ? 'bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200' : 'hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400'}`}
                            title={vn ? 'Cài đặt' : 'Settings'}
                        >
                            <Settings size={15} />
                        </button>
                    )}
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 transition-colors">
                        <X size={15} />
                    </button>
                </div>

                {/* Tab navigation */}
                {!confirmScreen && connected && (
                    <div className="flex border-b dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                        <button
                            onClick={() => setActiveTab('notebooks')}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'notebooks' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            <Github size={13} />
                            {vn ? 'Notebooks' : 'Notebooks'}
                        </button>
                        <button
                            onClick={() => setActiveTab('diary')}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'diary' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            <BookMarked size={13} />
                            {vn ? 'Nhật ký' : 'Diary'}
                        </button>
                    </div>
                )}

                {/* ── Confirm publish screen (replaces body+footer) ── */}
                {confirmScreen ? (<>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            {vn ? `${checked.size} notebook sẽ được đẩy lên GitHub` : `${checked.size} notebooks will be pushed to GitHub`}
                        </p>

                        {/* Destination repo — shown prominently so user can verify before publishing */}
                        <div className="flex items-center gap-2.5 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
                            <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider mb-0.5">
                                    {vn ? 'Đẩy lên repo' : 'Publishing to'}
                                </p>
                                <p className="text-xs font-mono text-amber-800 dark:text-amber-300 font-bold truncate">
                                    {githubStatus?.repo} / {githubStatus?.branch}
                                </p>
                            </div>
                        </div>

                        {/* Notebook rows */}
                        <div className="space-y-1.5">
                            {notebooks.filter(nb => checked.has(nb.id)).map(nb => {
                                const status = nbStatus(nb);
                                return (
                                    <div key={nb.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700/40 border border-gray-100 dark:border-slate-600">
                                        <BookOpen size={12} className="text-gray-400 shrink-0" />
                                        <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">{nb.title}</span>
                                        {nb.nodeCount != null && <span className="text-[11px] text-gray-400 shrink-0">{nb.nodeCount} notes</span>}
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                                            status === 'new' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                            : status === 'changed' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400'
                                            : 'bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-gray-400'}`}>
                                            {status === 'new' ? (vn ? 'MỚI' : 'NEW')
                                            : status === 'changed' ? (vn ? 'CÓ THAY ĐỔI' : 'CHANGED')
                                            : (vn ? 'KHÔNG ĐỔI' : 'UNCHANGED')}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Commit message */}
                        <div className="space-y-1.5 pt-1">
                            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 block">
                                Commit message
                            </label>
                            <textarea
                                value={commitMessage}
                                onChange={e => setCommitMessage(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl resize-y outline-none focus:border-blue-400 text-gray-700 dark:text-gray-300 font-mono leading-relaxed"
                            />
                            <p className="text-[10px] text-gray-400">
                                {vn ? 'Ghi lại những gì bạn học hôm nay — sẽ xuất hiện trong git history của repo.' : 'Describe what you learned today — appears in the repo git history.'}
                            </p>
                        </div>
                    </div>

                    {/* Confirm footer */}
                    <div className="shrink-0 px-4 py-3 border-t dark:border-slate-700 bg-white dark:bg-slate-800 flex gap-2">
                        <button
                            onClick={() => setConfirmScreen(false)}
                            className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            {vn ? 'Quay lại' : 'Back'}
                        </button>
                        <button
                            onClick={() => {
                                const selected = notebooks.filter(nb => checked.has(nb.id));
                                const ids = selected.map(nb => nb.id);
                                const newCounts: Record<string, number> = {};
                                const newFolders = new Set(publishedFolders);
                                selected.forEach(nb => {
                                    newCounts[nb.id] = nb.nodeCount ?? 0;
                                    newFolders.add(toSnakeCase(nb.title));
                                });
                                setLocalPublishedCounts(prev => ({ ...prev, ...newCounts }));
                                setPublishedFolders(newFolders);
                                setConfirmScreen(false);
                                onBulkPublish(ids, bulkMode, commitMessage);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors"
                        >
                            <Github size={13} />
                            Publish {checked.size}
                        </button>
                    </div>
                </>) : (<>

                {activeTab === 'diary' ? (
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {diaryLoading ? (
                            <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
                        ) : diaryEntries.length === 0 ? (
                            <div className="text-center py-16 space-y-3">
                                <BookMarked size={40} className="text-gray-200 dark:text-gray-700 mx-auto" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">{vn ? 'Nhật ký trống' : 'Diary is empty'}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 max-w-xs mx-auto leading-relaxed">
                                    {vn
                                        ? 'Mỗi lần bạn publish notebook, AI sẽ tóm tắt những gì bạn học được và lưu vào đây.'
                                        : 'Each time you publish a notebook, AI will summarize what you learned and save it here.'}
                                </p>
                            </div>
                        ) : (() => {
                            // Group entries by calendar day — use dayKey for new format, derive from publishedAt for legacy
                            const toMs = (v: any): number =>
                                typeof v === 'number' ? v :
                                typeof v?.seconds === 'number' ? v.seconds * 1000 : 0;
                            const byDay = new Map<string, DiaryEntry[]>();
                            for (const e of diaryEntries) {
                                const key = e.dayKey || new Date(toMs(e.publishedAt)).toISOString().slice(0, 10);
                                if (!byDay.has(key)) byDay.set(key, []);
                                byDay.get(key)!.push(e);
                            }
                            const days = [...byDay.entries()].sort((a, b) => b[0].localeCompare(a[0]));

                            // Stats
                            const publishDates = new Set(diaryEntries.map(e => new Date(toMs(e.publishedAt)).toDateString()));
                            const totalDays = days.length;
                            const streak = (() => {
                                let s = 0;
                                const d = new Date(); d.setHours(0, 0, 0, 0);
                                while (publishDates.has(new Date(d).toDateString())) { s++; d.setDate(d.getDate() - 1); }
                                return s;
                            })();

                            // Contribution graph
                            const today = new Date(); today.setHours(23, 59, 59, 999);
                            const earliestMs = days.length ? new Date(days[days.length - 1][0]).getTime() : today.getTime();
                            const msPerWeek = 7 * 24 * 60 * 60 * 1000;
                            const WEEKS = Math.max(8, Math.ceil((today.getTime() - earliestMs) / msPerWeek) + 1);
                            const cells: { date: Date; active: boolean }[] = [];
                            for (let w = WEEKS - 1; w >= 0; w--) {
                                for (let d = 0; d < 7; d++) {
                                    const date = new Date(today);
                                    date.setDate(today.getDate() - (w * 7 + (6 - d)));
                                    cells.push({ date, active: publishDates.has(date.toDateString()) });
                                }
                            }

                            return (
                                <>
                                    {/* Stats */}
                                    <div className="flex gap-3">
                                        <div className="flex-1 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                                            <div className="text-2xl font-black text-gray-800 dark:text-white">{totalDays}</div>
                                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">{vn ? 'Ngày học' : 'Days studied'}</div>
                                        </div>
                                        <div className="flex-1 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                                            <div className="text-2xl font-black text-orange-500">{streak}</div>
                                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">{vn ? 'Streak ngày' : 'Day streak'}</div>
                                        </div>
                                    </div>

                                    {/* Contribution graph */}
                                    <div className="flex gap-0.5 overflow-x-auto pb-1">
                                        {Array.from({ length: WEEKS }).map((_, w) => (
                                            <div key={w} className="flex flex-col gap-0.5 shrink-0">
                                                {cells.slice(w * 7, w * 7 + 7).map((cell, d) => (
                                                    <div key={d}
                                                        title={cell.date.toLocaleDateString(vn ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric' }) + (cell.active ? ' ✓' : '')}
                                                        className={`w-3 h-3 rounded-sm ${cell.active ? 'bg-green-500 dark:bg-green-400' : 'bg-gray-100 dark:bg-slate-700'}`}
                                                    />
                                                ))}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Day-by-day journal */}
                                    <div className="space-y-1">
                                        {days.map(([dateKey, entries], idx) => {
                                            const prevDateKey = idx < days.length - 1 ? days[idx + 1][0] : null;
                                            const gapDays = prevDateKey
                                                ? Math.round((new Date(dateKey).getTime() - new Date(prevDateKey).getTime()) / 86400000) - 1
                                                : 0;
                                            const dayDate = new Date(dateKey + 'T12:00:00');

                                            // Support new consolidated format (has notebooks[]) and legacy format
                                            const dailyEntry = entries.find(e => e.notebooks && Array.isArray(e.notebooks));
                                            const notebooks = dailyEntry
                                                ? dailyEntry.notebooks!.map(n => n.title)
                                                : [...new Set(entries.map(e => e.notebookTitle).filter(Boolean) as string[])];
                                            const summaries = dailyEntry
                                                ? (dailyEntry.aiSummary ? [dailyEntry.aiSummary] : [])
                                                : entries.map(e => e.aiSummary).filter(Boolean) as string[];
                                            const totalChanged = dailyEntry
                                                ? dailyEntry.notebooks!.reduce((s, n) => s + (n.changedCount || 0), 0)
                                                : entries.reduce((s, e) => s + (e.changedCount || 0), 0);

                                            return (
                                                <React.Fragment key={dateKey}>
                                                    <div className="rounded-xl bg-gray-50 dark:bg-slate-700/40 p-3 space-y-2">
                                                        {/* Date header */}
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                                                                {dayDate.toLocaleDateString(vn ? 'vi-VN' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                            {totalChanged > 0 && (
                                                                <span className="ml-auto text-[10px] text-gray-400 shrink-0">
                                                                    +{totalChanged} {vn ? 'ghi chú' : 'notes'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Notebooks studied */}
                                                        <div className="flex flex-wrap gap-1 pl-4">
                                                            {notebooks.map(nb => (
                                                                <span key={nb} className="text-[10px] px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full font-medium truncate max-w-[180px]">
                                                                    {nb}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        {/* AI summaries */}
                                                        {summaries.map((s, i) => (
                                                            <p key={i} className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed italic pl-4">
                                                                &ldquo;{s}&rdquo;
                                                            </p>
                                                        ))}
                                                    </div>
                                                    {/* Gap indicator */}
                                                    {gapDays >= 2 && (
                                                        <div className="flex items-center gap-2 py-1 px-3">
                                                            <div className="flex-1 border-t border-dashed border-gray-200 dark:border-slate-700" />
                                                            <span className="text-[10px] text-gray-300 dark:text-gray-600 shrink-0">
                                                                {vn ? `${gapDays} ngày không học` : `${gapDays}-day gap`}
                                                            </span>
                                                            <div className="flex-1 border-t border-dashed border-gray-200 dark:border-slate-700" />
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                ) : (<>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">

                    {/* Not connected */}
                    {!connected ? (
                        <div className="text-center py-6 space-y-4">
                            <Github size={44} className="text-gray-300 dark:text-gray-600 mx-auto" />
                            <div>
                                <p className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                                    {vn ? 'Chưa kết nối GitHub' : 'GitHub not connected'}
                                </p>
                                <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
                                    {vn
                                        ? 'Kết nối GitHub để publish notebook thành Markdown repo đẹp. Không cần token.'
                                        : 'Connect GitHub to publish your notebooks as a clean Markdown repo. No token needed.'}
                                </p>
                            </div>
                            <a
                                href={getGitHubInstallUrl()}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 dark:hover:bg-white transition-colors"
                            >
                                <Github size={15} />
                                {vn ? 'Kết nối GitHub' : 'Connect GitHub'}
                            </a>

                            {/* Fallback for already-installed app */}
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                                    {vn
                                        ? 'Đã cài app rồi nhưng không redirect về đây? Nhập thủ công Installation ID:'
                                        : 'Already installed but not redirected back? Enter Installation ID manually:'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                                    {vn
                                        ? 'Copy số cuối trong URL GitHub: /settings/installations/XXXXXXX'
                                        : 'Copy the last number from GitHub URL: /settings/installations/XXXXXXX'}
                                </p>
                                <div className="flex gap-2 max-w-xs mx-auto">
                                    <input
                                        type="text"
                                        value={manualId}
                                        onChange={e => { setManualId(e.target.value); setManualError(''); }}
                                        placeholder="e.g. 12345678"
                                        className="flex-1 text-xs px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    <button
                                        disabled={!manualId.trim() || manualLinking}
                                        onClick={async () => {
                                            const id = parseInt(manualId.trim(), 10);
                                            if (isNaN(id)) { setManualError(vn ? 'ID không hợp lệ' : 'Invalid ID'); return; }
                                            setManualLinking(true);
                                            setManualError('');
                                            try {
                                                await linkGitHubInstallation(id);
                                            } catch (e: any) {
                                                setManualError(e.message || (vn ? 'Kết nối thất bại' : 'Link failed'));
                                            } finally {
                                                setManualLinking(false);
                                            }
                                        }}
                                        className="px-3 py-2 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 text-xs rounded-lg disabled:opacity-40 flex items-center gap-1"
                                    >
                                        {manualLinking ? <Loader2 size={12} className="animate-spin" /> : null}
                                        {vn ? 'Kết nối' : 'Link'}
                                    </button>
                                </div>
                                {manualError && <p className="text-xs text-red-500 mt-1">{manualError}</p>}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Settings panel — collapsible */}
                            {settingsOpen && (
                            <div className="border-b dark:border-slate-700 px-5 py-4 space-y-4 bg-gray-50/60 dark:bg-slate-900/40 shrink-0">

                            {/* Connection status */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                                    <CheckCircle2 size={12} />
                                    <span>{vn ? 'Đã kết nối' : 'Connected'}{githubStatus!.connectedAs ? ` @${githubStatus!.connectedAs}` : ''}</span>
                                </div>
                                <button onClick={async () => { await onClearGitHubConfig(); }}
                                    className="text-[11px] text-red-400 hover:text-red-500 transition-colors">
                                    {vn ? 'Ngắt kết nối' : 'Disconnect'}
                                </button>
                            </div>

                            {/* Repo config (shown when not yet configured) */}
                            {!configured && (
                                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl space-y-3">
                                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                                        {vn ? '⚠️ Chọn repo để bắt đầu publish' : '⚠️ Select a repo to start publishing'}
                                    </p>
                                    {(githubStatus!.availableRepos?.length ?? 0) > 0 ? (
                                        <select
                                            value={ghRepo}
                                            onChange={e => setGhRepo(e.target.value)}
                                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg font-mono outline-none focus:border-blue-400"
                                        >
                                            <option value="">{vn ? '— Chọn repo —' : '— Select repo —'}</option>
                                            {githubStatus!.availableRepos!.map(r => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text" value={ghRepo} onChange={e => setGhRepo(e.target.value)}
                                            placeholder="username/my-notes"
                                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg font-mono outline-none focus:border-blue-400"
                                        />
                                    )}
                                    <div className="flex gap-2">
                                        <input
                                            type="text" value={ghBranch} onChange={e => setGhBranch(e.target.value)}
                                            placeholder="main"
                                            className="flex-1 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg font-mono outline-none focus:border-blue-400"
                                        />
                                        <button
                                            onClick={async () => {
                                                if (!ghRepo) return;
                                                setSavingRepo(true);
                                                await onUpdateGitHubRepo(ghRepo, ghBranch || 'main');
                                                setSavingRepo(false);
                                                setSavedRepo(true);
                                                setTimeout(() => setSavedRepo(false), 2000);
                                            }}
                                            disabled={!ghRepo || savingRepo}
                                            className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            {savingRepo ? <Loader2 size={13} className="animate-spin" /> : savedRepo ? <CheckCircle2 size={13} /> : null}
                                            {vn ? 'Lưu' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Repo change (when already configured) */}
                            {configured && (
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-700/40 border border-gray-200 dark:border-slate-600 rounded-xl">
                                        <select
                                            value={ghRepo || githubStatus!.repo || ''}
                                            onChange={e => { setGhRepo(e.target.value); setRepoSaveConfirm(false); }}
                                            className={`flex-1 px-2 py-1.5 text-xs bg-white dark:bg-slate-800 border rounded-lg font-mono outline-none ${ghRepo && ghRepo !== (githubStatus!.repo || '') ? 'border-orange-400 dark:border-orange-500' : 'border-gray-300 dark:border-slate-600'}`}
                                        >
                                            {(githubStatus!.availableRepos?.length ?? 0) > 0
                                                ? githubStatus!.availableRepos!.map(r => <option key={r} value={r}>{r}</option>)
                                                : <option value={githubStatus!.repo || ''}>{githubStatus!.repo}</option>
                                            }
                                        </select>
                                        <input
                                            type="text"
                                            value={ghBranch || githubStatus!.branch || 'main'}
                                            onChange={e => setGhBranch(e.target.value)}
                                            className="w-20 px-2 py-1.5 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg font-mono outline-none"
                                        />
                                        <button
                                            onClick={async () => {
                                                const repo = ghRepo || githubStatus!.repo || '';
                                                const isChangingRepo = repo !== (githubStatus?.repo || '');
                                                if (!repo) return;
                                                if (isChangingRepo && !repoSaveConfirm) {
                                                    setRepoSaveConfirm(true);
                                                    return;
                                                }
                                                setRepoSaveConfirm(false);
                                                setSavingRepo(true);
                                                await onUpdateGitHubRepo(repo, ghBranch || githubStatus!.branch || 'main');
                                                setSavingRepo(false);
                                                setSavedRepo(true);
                                                setTimeout(() => setSavedRepo(false), 2000);
                                            }}
                                            disabled={savingRepo}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-lg disabled:opacity-50 flex items-center gap-1 ${repoSaveConfirm ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'}`}
                                        >
                                            {savingRepo ? <Loader2 size={11} className="animate-spin" /> : savedRepo ? <CheckCircle2 size={11} className="text-green-400" /> : null}
                                            {repoSaveConfirm ? (vn ? 'Xác nhận?' : 'Confirm?') : (vn ? 'Lưu' : 'Save')}
                                        </button>
                                    </div>
                                    {ghRepo && ghRepo !== (githubStatus!.repo || '') && (
                                        <div className="flex items-start gap-1.5 px-1">
                                            <AlertTriangle size={11} className="text-orange-500 shrink-0 mt-0.5" />
                                            <p className="text-[11px] text-orange-600 dark:text-orange-400">
                                                {repoSaveConfirm
                                                    ? (vn ? 'Nhấn "Xác nhận?" để đổi repo publish. Thao tác này sẽ ảnh hưởng đến tất cả lần publish sau!' : 'Click "Confirm?" to change publish repo. This affects ALL future publishes!')
                                                    : (vn ? 'Cảnh báo: đây là repo KHÁC với repo hiện tại. Nhấn Lưu một lần nữa để xác nhận.' : 'Warning: this is a DIFFERENT repo. Click Save again to confirm the change.')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* README Editor */}
                            {connected && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between px-1">
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                            {vn ? 'README — Trang chủ repo' : 'README — Repo homepage'}
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    saveGitHubRepoDescription(repoDesc).catch(() => {});
                                                    regenerateReadme().catch(console.error);
                                                    alert(vn
                                                        ? 'README đang được cập nhật trên server (~1-2 phút). Bạn có thể đóng modal và tiếp tục làm việc.'
                                                        : 'README is updating on the server (~1-2 min). You can close this modal and continue working.');
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                                            >
                                                <RefreshCw size={11} />
                                                {vn ? 'Cập nhật README' : 'Push README'}
                                            </button>
                                            <button
                                                onClick={() => setReadmeEditorOpen(true)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                            >
                                                <Edit3 size={11} />
                                                {vn ? 'Chỉnh sửa' : 'Edit README'}
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        onClick={() => setReadmeEditorOpen(true)}
                                        className="cursor-pointer px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        {repoDesc ? (
                                            <p className="text-xs text-gray-600 dark:text-gray-300 font-mono leading-relaxed whitespace-pre-wrap line-clamp-3">{repoDesc}</p>
                                        ) : (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">{vn ? 'Chưa có nội dung — click để viết trang chủ repo của bạn' : 'No content yet — click to write your repo homepage'}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Group Order */}
                            {connected && allGroups.length > 1 && (
                                <div className="space-y-2">
                                    <div className="flex items-center px-1">
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                            {vn ? 'Thứ tự nhóm trong README' : 'Group order in README'}
                                        </label>
                                    </div>
                                    <div className="space-y-1">
                                        {allGroups.map((g, i) => (
                                            <div key={g} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700/50">
                                                <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 font-medium">📂 {g}</span>
                                                <button
                                                    onClick={() => {
                                                        const newOrder = [...allGroups];
                                                        [newOrder[i - 1], newOrder[i]] = [newOrder[i], newOrder[i - 1]];
                                                        setLocalGroupOrder(newOrder);
                                                        saveGroupOrder(newOrder).catch(console.error);
                                                    }}
                                                    disabled={i === 0}
                                                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronUp size={13} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newOrder = [...allGroups];
                                                        [newOrder[i], newOrder[i + 1]] = [newOrder[i + 1], newOrder[i]];
                                                        setLocalGroupOrder(newOrder);
                                                        saveGroupOrder(newOrder).catch(console.error);
                                                    }}
                                                    disabled={i === allGroups.length - 1}
                                                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronDown size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            </div>
                            )}

                            {/* Notebooks list */}
                            {connected && (
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                            {vn ? 'Notebooks' : 'Notebooks'} ({notebooks.length})
                                        </p>
                                        <button
                                            onClick={() => {
                                                if (checked.size === notebooks.length) {
                                                    setChecked(new Set());
                                                } else {
                                                    setChecked(new Set(notebooks.map(nb => nb.id)));
                                                }
                                            }}
                                            className="text-[11px] text-blue-500 hover:underline"
                                        >
                                            {checked.size === notebooks.length ? (vn ? 'Bỏ chọn tất cả' : 'Deselect all') : (vn ? 'Chọn tất cả' : 'Select all')}
                                        </button>
                                    </div>
                                    {notebooks.length === 0 ? (
                                        <p className="text-xs text-gray-400 text-center py-4">{vn ? 'Chưa có notebook nào.' : 'No notebooks yet.'}</p>
                                    ) : (
                                        <div className="space-y-0.5">
                                        {notebooks.map((nb, idx) => {
                                            const desc = editingDesc[nb.id] !== undefined ? editingDesc[nb.id] : (nb.description || '');
                                            const isChecked = checked.has(nb.id);
                                            const status = nbStatus(nb);
                                            return (
                                                <div key={nb.id} className={`px-3 py-4 rounded-lg transition-colors ${
                                                    isChecked
                                                        ? 'bg-blue-100/70 dark:bg-blue-900/30'
                                                        : idx % 2 === 0
                                                            ? 'bg-gray-100 dark:bg-slate-700/60'
                                                            : 'bg-gray-50/40 dark:bg-slate-800/20'
                                                }`}>
                                                    {/* Title row */}
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setChecked(prev => { const s = new Set(prev); s.has(nb.id) ? s.delete(nb.id) : s.add(nb.id); return s; })}
                                                            className="shrink-0 text-gray-300 dark:text-gray-600 hover:text-blue-500 transition-colors"
                                                        >
                                                            {isChecked ? <CheckSquare size={15} className="text-blue-500" /> : <Square size={15} />}
                                                        </button>
                                                        <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{nb.title}</span>
                                                        {/* Status badge */}
                                                        {(() => {
                                                            const lastCount = getLastCount(nb);
                                                            const delta = lastCount != null ? (nb.nodeCount ?? 0) - lastCount : null;
                                                            let deltaStr: string;
                                                            if (delta != null && delta !== 0) {
                                                                deltaStr = delta > 0 ? `+${delta} notes` : `${delta} notes`;
                                                            } else if (nb.changedNodeCount && nb.changedNodeCount > 0) {
                                                                deltaStr = `~${nb.changedNodeCount} edited`;
                                                            } else {
                                                                deltaStr = 'UPDATED';
                                                            }
                                                            return (
                                                                <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                                                    status === 'new'
                                                                        ? 'bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-gray-400'
                                                                        : status === 'changed'
                                                                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                                                                        : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500'
                                                                }`}>
                                                                    {status === 'new' ? 'UNPUBLISHED' : status === 'changed' ? deltaStr : 'UP TO DATE'}
                                                                </span>
                                                            );
                                                        })()}
                                                        {/* Per-notebook Publish button — subtle outline, B&W */}
                                                        <button
                                                            onClick={() => {
                                                                if (!configured) return;
                                                                setLocalPublishedCounts(prev => ({ ...prev, [nb.id]: nb.nodeCount ?? 0 }));
                                                                setPublishedFolders(prev => new Set([...prev, toSnakeCase(nb.title)]));
                                                                onPublishNotebook(nb.id);
                                                            }}
                                                            disabled={!configured}
                                                            title={configured ? undefined : (vn ? 'Chọn repo trước' : 'Select a repo first')}
                                                            className="shrink-0 flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border border-gray-400 dark:border-gray-500 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-gray-900 hover:border-gray-800 dark:hover:border-gray-200 transition-colors disabled:opacity-40"
                                                        >
                                                            <Github size={10} />
                                                            Publish
                                                        </button>
                                                        {/* Unpublish (remove from GitHub) — only for published notebooks */}
                                                        {status !== 'new' && (
                                                            unpublishConfirm === nb.id ? (
                                                                <button
                                                                    onClick={async () => {
                                                                        setUnpublishConfirm(null);
                                                                        setUnpublishing(nb.id);
                                                                        try {
                                                                            await unpublishNotebookFromGitHub(nb.id);
                                                                            setPublishedFolders(prev => {
                                                                                const s = new Set(prev);
                                                                                s.delete(toSnakeCase(nb.title));
                                                                                return s;
                                                                            });
                                                                            setLocalPublishedCounts(prev => {
                                                                                const n = { ...prev };
                                                                                delete n[nb.id];
                                                                                return n;
                                                                            });
                                                                            onUnpublishNotebook?.(nb.id);
                                                                        } catch (e: any) {
                                                                            alert(e.message || 'Unpublish failed');
                                                                        } finally {
                                                                            setUnpublishing(null);
                                                                        }
                                                                    }}
                                                                    disabled={unpublishing === nb.id}
                                                                    className="shrink-0 flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border border-red-400 rounded text-red-600 dark:text-red-400 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                                                                >
                                                                    {unpublishing === nb.id ? <Loader2 size={10} className="animate-spin" /> : null}
                                                                    {vn ? 'Xác nhận?' : 'Confirm?'}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => setUnpublishConfirm(nb.id)}
                                                                    title={vn ? 'Xóa khỏi GitHub' : 'Remove from GitHub'}
                                                                    className="shrink-0 p-1 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                                                >
                                                                    <Trash2 size={11} />
                                                                </button>
                                                            )
                                                        )}
                                                    </div>

                                                    {/* Description row */}
                                                    <div className="flex items-start gap-1.5 mt-1 pl-5">
                                                        <div className="flex-1 min-w-0 cursor-text" onClick={() => setExpandedNbDesc(nb.id)}>
                                                            {expandedNbDesc === nb.id ? (
                                                                <textarea
                                                                    autoFocus
                                                                    value={desc}
                                                                    onChange={e => {
                                                                        setEditingDesc(prev => ({ ...prev, [nb.id]: e.target.value }));
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    onFocus={e => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    onBlur={() => {
                                                                        const val = editingDesc[nb.id];
                                                                        if (val !== undefined && val !== (nb.description || '')) {
                                                                            onUpdateNotebookDescription(nb.id, val);
                                                                        }
                                                                        setExpandedNbDesc(null);
                                                                    }}
                                                                    onClick={e => e.stopPropagation()}
                                                                    placeholder={vn ? 'Mô tả notebook...' : 'Notebook description...'}
                                                                    rows={2}
                                                                    className="w-full px-0 py-0.5 text-xs bg-transparent border-0 border-b border-gray-200 dark:border-slate-600 outline-none text-gray-600 dark:text-gray-400 placeholder-gray-300 dark:placeholder-gray-600 resize-none overflow-hidden leading-snug"
                                                                />
                                                            ) : (
                                                                <p className={`text-xs leading-snug ${desc ? 'text-gray-500 dark:text-gray-400 line-clamp-1' : 'text-gray-400 dark:text-gray-600 italic'}`}>
                                                                    {desc || (vn ? 'Thêm mô tả...' : 'Add description...')}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {/* Pencil — always visible */}
                                                        <button
                                                            onMouseDown={e => e.preventDefault()}
                                                            onClick={() => setExpandedNbDesc(expandedNbDesc === nb.id ? null : nb.id)}
                                                            className="shrink-0 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors mt-0.5"
                                                            title={vn ? 'Chỉnh sửa mô tả' : 'Edit description'}
                                                        >
                                                            <Edit3 size={11} />
                                                        </button>
                                                        {/* Sparkles — only visible when editing */}
                                                        {expandedNbDesc === nb.id && (
                                                            <button
                                                                onMouseDown={e => e.preventDefault()}
                                                                onClick={async () => {
                                                                    setGeneratingNbDesc(nb.id);
                                                                    try {
                                                                        const lang = vn ? 'both' : 'en';
                                                                        const d = await generateNotebookDescription(nb.id, lang);
                                                                        setEditingDesc(prev => ({ ...prev, [nb.id]: d }));
                                                                        onUpdateNotebookDescription(nb.id, d);
                                                                    } catch (e: any) {
                                                                        alert(e.message || 'AI generation failed');
                                                                    } finally {
                                                                        setGeneratingNbDesc(null);
                                                                    }
                                                                }}
                                                                disabled={generatingNbDesc === nb.id}
                                                                className="shrink-0 text-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50 mt-0.5"
                                                                title={vn ? 'AI tạo mô tả tự động' : 'AI generate description'}
                                                            >
                                                                {generatingNbDesc === nb.id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Group row */}
                                                    <div className="flex items-center gap-1.5 mt-1 pl-5">
                                                        {(() => {
                                                            const groupVal = editingGroup[nb.id] !== undefined ? editingGroup[nb.id] : (nb.githubGroup || '');
                                                            const allGroups = [...new Set(notebooks.map(n => n.githubGroup).filter(Boolean) as string[])];
                                                            return expandedNbGroup === nb.id ? (
                                                                <>
                                                                    <input
                                                                        autoFocus
                                                                        list={`groups-${nb.id}`}
                                                                        value={groupVal}
                                                                        onChange={e => setEditingGroup(prev => ({ ...prev, [nb.id]: e.target.value }))}
                                                                        onBlur={() => {
                                                                            const val = editingGroup[nb.id] ?? nb.githubGroup ?? '';
                                                                            onUpdateNotebookGroup(nb.id, val);
                                                                            setExpandedNbGroup(null);
                                                                        }}
                                                                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                                        placeholder={vn ? 'Tên nhóm...' : 'Group name...'}
                                                                        className="flex-1 text-[11px] px-1.5 py-0.5 bg-transparent border-b border-gray-200 dark:border-slate-600 outline-none text-gray-600 dark:text-gray-400 placeholder-gray-300"
                                                                    />
                                                                    <datalist id={`groups-${nb.id}`}>
                                                                        {allGroups.map(g => <option key={g} value={g} />)}
                                                                    </datalist>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    onClick={() => { setEditingGroup(prev => ({ ...prev, [nb.id]: nb.githubGroup || '' })); setExpandedNbGroup(nb.id); }}
                                                                    className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                                                                >
                                                                    {groupVal ? (
                                                                        <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500 dark:text-indigo-400 font-medium">{groupVal}</span>
                                                                    ) : (
                                                                        <span className="italic">{vn ? '+ Thêm nhóm' : '+ Add group'}</span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>

                                                    {nb.lastPublishedAt && (
                                                        <div className="flex items-center gap-2 mt-1 pl-5">
                                                            <Clock size={10} className="text-gray-400 shrink-0" />
                                                            <span className="text-[11px] text-gray-400">{formatDate(nb.lastPublishedAt)}</span>
                                                            {githubStatus?.repo && githubStatus?.branch && (
                                                                <a
                                                                    href={`https://github.com/${githubStatus.repo}/tree/${githubStatus.branch}/${toSnakeCase(nb.title)}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={vn ? 'Mở notebook này trên GitHub' : 'Open this notebook on GitHub'}
                                                                    onClick={e => e.stopPropagation()}
                                                                    className="flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                                                >
                                                                    <ExternalLink size={10} />
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        </div>
                                    )}
                                </div>
                            )}

                        </>
                    )}
                </div>

                {/* Bottom bar */}
                {configured && (
                    <div className="shrink-0 border-t dark:border-slate-700 bg-white dark:bg-slate-800">
                        {/* Copyright warning */}
                        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 flex items-start gap-1.5">
                            <AlertTriangle size={10} className="mt-0.5 text-amber-500 shrink-0" />
                            <p className="text-[10px] text-amber-700 dark:text-amber-400 leading-snug">
                                {vn ? 'Nếu publish lên repo public, hãy đảm bảo bạn có quyền chia sẻ ảnh từ sách/PDF bản quyền.' : 'If publishing to a public repo, ensure you have rights to share screenshots from copyrighted books/PDFs.'}
                            </p>
                        </div>
                        {/* Actions row */}
                        <div className="px-4 py-3 space-y-2">
                            {/* Full / Safe toggle with explanation */}
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 dark:bg-slate-700 rounded-lg shrink-0">
                                    <button
                                        onClick={() => setBulkMode('full')}
                                        className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${bulkMode === 'full' ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                    >
                                        <Shield size={10} /> Full
                                    </button>
                                    <button
                                        onClick={() => setBulkMode('safe')}
                                        className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${bulkMode === 'safe' ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                    >
                                        <Globe size={10} /> Safe
                                    </button>
                                </div>
                                <p className="text-[11px] text-gray-400 leading-snug">
                                    {bulkMode === 'full'
                                        ? (vn ? 'Xuất toàn bộ — notes, ảnh chụp sách/PDF, AI feedback' : 'Exports everything — notes, book/PDF screenshots, AI feedback')
                                        : (vn ? 'Chỉ xuất text & AI notes — không có ảnh chụp sách/PDF' : 'Text & AI notes only — no book/PDF screenshots')}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-400 flex-1">
                                {checked.size > 0 ? `${checked.size} ${vn ? 'notebook' : 'notebooks'}` : (vn ? 'Chưa chọn notebook' : 'None selected')}
                            </p>
                            {checked.size > 0 && (
                                <button
                                    onClick={() => {
                                        const selected = notebooks.filter(nb => checked.has(nb.id));
                                        setCommitMessage(buildCommitMessage(selected));
                                        setConfirmScreen(true);
                                    }}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-700 dark:hover:bg-white transition-colors"
                                >
                                    <Github size={13} />
                                    Publish {checked.size}
                                </button>
                            )}
                            </div>
                        </div>
                    </div>
                )}
                </>)}
                </>)}
            </div>
        </div>

        {/* README Chat Editor — outside hub modal to avoid overflow-hidden clipping */}
        {readmeEditorOpen && (
            <div className="fixed inset-0 z-[10300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[82vh] flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-800 shrink-0">
                        <div className="flex items-center gap-2">
                            <Github size={16} className="text-gray-500 dark:text-gray-400" />
                            <span className="font-bold text-sm dark:text-white">{vn ? 'Chỉnh sửa trang chủ repo' : 'Edit README'}</span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">{repoDesc.length} {vn ? 'ký tự' : 'chars'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={async () => {
                                await saveGitHubRepoDescription(repoDesc).catch(console.error);
                                setReadmeEditorOpen(false);
                            }} className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                                {vn ? 'Lưu & đóng' : 'Save & close'}
                            </button>
                            <button onClick={() => setReadmeEditorOpen(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-400">
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Split body */}
                    <div className="flex-1 flex overflow-hidden">
                        {/* Left: Markdown editor / preview */}
                        <div className="flex-1 flex flex-col border-r dark:border-slate-700 min-w-0">
                            <div className="flex items-center justify-between px-2 py-1.5 border-b dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 shrink-0">
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setReadmeViewMode('edit')}
                                        className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-colors ${readmeViewMode === 'edit' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                                        <Edit3 size={10} /> {vn ? 'Soạn thảo' : 'Edit'}
                                    </button>
                                    <button onClick={() => setReadmeViewMode('preview')}
                                        className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-colors ${readmeViewMode === 'preview' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                                        <Eye size={10} /> {vn ? 'Xem trước' : 'Preview'}
                                    </button>
                                </div>
                                <button onClick={() => setShowMdHelp(v => !v)}
                                    className={`flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-lg transition-colors ${showMdHelp ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                                    <HelpCircle size={11} /> {vn ? 'Cú pháp Markdown' : 'Markdown syntax'}
                                </button>
                            </div>

                            {showMdHelp && (
                                <div className="px-4 py-2.5 border-b dark:border-slate-700 bg-indigo-50/40 dark:bg-indigo-900/10 shrink-0 grid grid-cols-2 gap-x-4 gap-y-1">
                                    {MARKDOWN_CHEATSHEET.map(row => (
                                        <div key={row.syntax} className="flex items-center gap-1.5 text-[10px]">
                                            <code className="px-1 py-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded text-gray-600 dark:text-gray-300 whitespace-pre font-mono">{row.syntax}</code>
                                            <span className="text-gray-400">→</span>
                                            <span className="text-gray-500 dark:text-gray-400">{row.result}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {readmeViewMode === 'edit' ? (
                                <textarea
                                    ref={textareaRef}
                                    autoFocus
                                    value={repoDesc}
                                    onChange={e => setRepoDesc(e.target.value)}
                                    placeholder={vn
                                        ? `# Tên repo của bạn\n\nViết về hành trình học tập của bạn...\n\nVí dụ:\n- Bạn đang học gì?\n- Tại sao bạn học?\n- Repo này chứa gì?\n\nHãy nhờ AI bên phải giúp bạn viết!`
                                        : `# Your repo name\n\nWrite about your learning journey...\n\nFor example:\n- What are you studying?\n- Why are you learning this?\n- What does this repo contain?\n\nAsk the AI on the right to help you write!`}
                                    className="flex-1 px-6 py-5 text-sm font-mono bg-white dark:bg-slate-900 text-gray-800 dark:text-gray-200 outline-none resize-none leading-relaxed placeholder-gray-300 dark:placeholder-gray-600"
                                />
                            ) : (
                                <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-900">
                                    {repoDesc.trim() ? (
                                        <>
                                            <style>{`
                                                .readme-preview { padding: 20px 28px; color: inherit; }
                                                .readme-preview h1, .readme-preview h2, .readme-preview h3 { font-weight: 700; margin: 1em 0 0.4em; line-height: 1.3; }
                                                .readme-preview h1 { font-size: 1.6em; border-bottom: 1px solid rgba(128,128,128,0.25); padding-bottom: 0.25em; }
                                                .readme-preview h2 { font-size: 1.3em; border-bottom: 1px solid rgba(128,128,128,0.15); padding-bottom: 0.2em; }
                                                .readme-preview h3 { font-size: 1.1em; }
                                                .readme-preview p { margin: 0.7em 0; line-height: 1.65; font-size: 0.92em; }
                                                .readme-preview a { color: #6366f1; text-decoration: underline; }
                                                .readme-preview code { background: rgba(128,128,128,0.15); padding: 0.15em 0.4em; border-radius: 4px; font-size: 0.85em; font-family: ui-monospace, monospace; }
                                                .readme-preview pre { background: rgba(128,128,128,0.12); padding: 12px 14px; border-radius: 8px; overflow-x: auto; margin: 0.8em 0; }
                                                .readme-preview pre code { background: none; padding: 0; }
                                                .readme-preview blockquote { border-left: 3px solid #6366f1; margin: 0.8em 0; padding: 0.2em 1em; opacity: 0.8; }
                                                .readme-preview ul, .readme-preview ol { margin: 0.6em 0; padding-left: 1.6em; font-size: 0.92em; line-height: 1.6; }
                                                .readme-preview li { margin: 0.2em 0; }
                                                .readme-preview hr { border: none; border-top: 1px solid rgba(128,128,128,0.25); margin: 1.2em 0; }
                                            `}</style>
                                            <div className="readme-preview text-gray-800 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(repoDesc) }} />
                                        </>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-xs text-gray-300 dark:text-gray-600">
                                            {vn ? 'Chưa có nội dung để xem trước' : 'Nothing to preview yet'}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="px-4 py-1.5 border-t dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 shrink-0">
                                <p className="text-[10px] text-gray-400">{vn ? 'Hỗ trợ Markdown · xuất hiện ở đầu README.md trên GitHub' : 'Supports Markdown · appears at the top of README.md on GitHub'}</p>
                            </div>
                        </div>

                        {/* Right: AI chat */}
                        <div className="w-80 flex flex-col bg-gray-50 dark:bg-slate-800/50 shrink-0 min-w-0">
                            <div className="px-3 pt-2.5 pb-2 border-b dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                    {vn ? 'Gợi ý nhanh' : 'Quick prompts'}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    {quickChips.map(chip => (
                                        <button key={chip} onClick={() => setChatInput(chip)}
                                            className="text-[10px] px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            {chip}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                                {chatMessages.length === 0 && (
                                    <div className="text-center py-10 space-y-2">
                                        <MessageSquare size={28} className="text-gray-200 dark:text-gray-700 mx-auto" />
                                        <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed px-4">
                                            {vn
                                                ? 'Nhờ AI giúp bạn viết README — giới thiệu hành trình học, liệt kê topic, thêm badge, v.v.'
                                                : 'Ask AI to help write your README — intro, topics, badges, any language.'}
                                        </p>
                                    </div>
                                )}
                                {chatMessages.map((msg, i) => {
                                    if (msg.role === 'user') return (
                                        <div key={i} className="flex justify-end">
                                            <div className="max-w-[88%] px-3 py-2 bg-indigo-600 text-white rounded-2xl rounded-br-sm text-xs leading-relaxed">
                                                {msg.content}
                                            </div>
                                        </div>
                                    );
                                    const insertMatch = msg.content.match(/<INSERT>([\s\S]*?)<\/INSERT>/);
                                    const insertContent = insertMatch?.[1]?.trim();
                                    const displayText = msg.content.replace(/<INSERT>[\s\S]*?<\/INSERT>/g, '').trim();
                                    return (
                                        <div key={i} className="flex flex-col gap-1.5">
                                            {displayText && (
                                                <div className="max-w-[92%] px-3 py-2 bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600 rounded-2xl rounded-tl-sm text-xs text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                                                    {displayText}
                                                </div>
                                            )}
                                            {insertContent && (
                                                <div className="border border-indigo-200 dark:border-indigo-700/60 rounded-xl overflow-hidden">
                                                    <pre className="px-3 py-2 text-[10px] font-mono text-gray-700 dark:text-gray-300 bg-indigo-50/60 dark:bg-indigo-900/20 whitespace-pre-wrap leading-relaxed max-h-36 overflow-y-auto">{insertContent}</pre>
                                                    <div className="px-2 py-1.5 bg-white dark:bg-slate-700 border-t border-indigo-100 dark:border-indigo-800 flex justify-end">
                                                        <button onClick={() => insertAtCursor(insertContent)}
                                                            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 rounded-lg transition-colors">
                                                            <ArrowDownToLine size={11} />
                                                            {vn ? 'Chèn vào editor' : 'Insert'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {chatLoading && (
                                    <div className="flex items-center gap-2 px-1 py-1">
                                        <Loader2 size={12} className="animate-spin text-indigo-400" />
                                        <span className="text-xs text-gray-400">{vn ? 'AI đang viết…' : 'AI is writing…'}</span>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>
                            <div className="px-3 py-2.5 border-t dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                                <div className="flex items-end gap-2">
                                    <textarea
                                        value={chatInput}
                                        onChange={e => setChatInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                                        placeholder={vn ? 'Nhắn gì đó với AI… (Enter để gửi)' : 'Ask AI something… (Enter to send)'}
                                        rows={2}
                                        className="flex-1 px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400 resize-none leading-relaxed focus:border-indigo-400 dark:focus:border-indigo-500"
                                    />
                                    <button onClick={handleChatSend} disabled={!chatInput.trim() || chatLoading}
                                        className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40 shrink-0 self-end">
                                        <Send size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};
